import React from "react";
import classNames from "classnames";
import grid from "../style.module.scss";
import themeStyles from "../theme/theme.module.scss";
import { attrFor, useHeadOfficeTheme } from "../theme/HeadOfficeThemeContext";
import { hoFontVariables } from "../theme/fonts";
import s from "./style.module.scss";

/* ------------------------------------------------------------------ *
 * shimmer primitives
 * ------------------------------------------------------------------ */

const Bar = ({
  w,
  h = 12,
  r = 6,
  mt = 0,
  circle = false,
}: {
  w: number | string;
  h?: number;
  r?: number;
  mt?: number;
  circle?: boolean;
}) => (
  <span
    className={s.sk}
    style={{
      width: typeof w === "number" ? `${w}px` : w,
      height: h,
      borderRadius: circle ? "50%" : r,
      marginTop: mt,
    }}
  />
);

const Card = ({ children }: { children: React.ReactNode }) => (
  <div className={s.card}>{children}</div>
);

const CardHead = ({ chip = true }: { chip?: boolean }) => (
  <div className={s.head}>
    <div>
      <Bar w={150} h={14} />
      <Bar w={110} h={10} mt={8} />
    </div>
    {chip && <Bar w={92} h={30} r={16} />}
  </div>
);

const ListRow = ({ avatar = false }: { avatar?: boolean }) => (
  <div className={s.row}>
    <Bar w={26} h={26} circle />
    {avatar && <Bar w={32} h={32} r={9} />}
    <div className={s.rowInfo}>
      <Bar w="70%" h={11} />
      <Bar w="45%" h={9} mt={7} />
    </div>
    <Bar w={54} h={12} />
  </div>
);

/* column-header strip for the Reader Campuses / Classes table */
const TableHead = () => (
  <div className={s.tableHead}>
    <Bar w={12} h={9} />
    <Bar w={72} h={9} />
    <Bar w={64} h={9} />
    <Bar w={100} h={9} />
    <Bar w={40} h={9} />
  </div>
);

const range = (n: number) => Array.from({ length: n });

/* ------------------------------------------------------------------ *
 * per-widget skeletons — each mirrors its real card, so it can replace
 * that widget individually while its API is still loading
 * ------------------------------------------------------------------ */

/** `count` mirrors the live KPI strip — head office/campus/teacher all show 6 */
export const KpiSkeleton = ({ count = 6 }: { count?: number }) => (
  <div className={s.kpiGrid}>
    {range(count).map((_, i) => (
      <div className={s.kpiCard} key={i}>
        <div className={s.kpiTop}>
          <Bar w={42} h={42} r={12} />
          <Bar w={44} h={18} r={16} />
        </div>
        <Bar w={80} h={26} />
        <Bar w="70%" h={11} mt={9} />
      </div>
    ))}
  </div>
);

export const GrowthSkeleton = () => (
  <Card>
    <CardHead />
    <div className={s.body}>
      <Bar w="100%" h={230} r={12} />
    </div>
  </Card>
);

export const MixSkeleton = () => (
  <Card>
    <CardHead />
    <div className={s.mixBody}>
      <Bar w={116} h={116} circle />
      <div className={s.legendCol}>
        {range(5).map((_, i) => (
          <Bar key={i} w="90%" h={12} mt={i ? 12 : 0} />
        ))}
      </div>
    </div>
  </Card>
);

export const CampusesSkeleton = () => (
  <Card>
    <CardHead chip={false} />
    <TableHead />
    {range(4).map((_, i) => (
      <ListRow avatar key={i} />
    ))}
  </Card>
);

export const AdoptionSkeleton = () => (
  <Card>
    <CardHead chip={false} />
    <div className={s.gaugeBody}>
      <Bar w={180} h={110} r={90} />
      <Bar w="60%" h={10} mt={14} />
      <div className={s.stats2}>
        <Bar w="100%" h={48} r={10} />
        <Bar w="100%" h={48} r={10} />
      </div>
    </div>
  </Card>
);

export const ListSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <Card>
    <CardHead chip={false} />
    {range(rows).map((_, i) => (
      <ListRow key={i} />
    ))}
  </Card>
);

export const ContentSkeleton = () => (
  <Card>
    <CardHead chip={false} />
    <div className={s.body}>
      <Bar w="100%" h={54} r={10} />
      {range(5).map((_, i) => (
        <Bar key={i} w="100%" h={14} mt={14} />
      ))}
    </div>
  </Card>
);

export const ComparisonSkeleton = () => (
  <Card>
    <CardHead chip={false} />
    <div className={s.body}>
      {range(6).map((_, i) => (
        <Bar key={i} w="100%" h={38} r={8} mt={i ? 8 : 0} />
      ))}
    </div>
  </Card>
);

/** rows skeleton for the "View All" modal body (paginated lists).
 *  defaults to 5 — the head-office page size — so it matches the real list */
export const ViewAllRowsSkeleton = ({ rows = 5 }: { rows?: number }) => (
  <div aria-busy="true">
    {range(rows).map((_, i) => (
      <div className={s.vrow} key={i}>
        <Bar w={24} h={24} r={7} />
        <div>
          <Bar w="70%" h={12} />
          <Bar w="45%" h={9} mt={6} />
        </div>
        <Bar w="90%" h={10} r={6} />
        <Bar w={44} h={12} />
      </div>
    ))}
  </div>
);

/** "View All" modal body for the Campus/Class Comparison heat table.
 *  The generic row skeleton (avatar + text lines) reads as a list, which the
 *  comparison grid isn't — this mirrors the real header + name + metric cells.
 *  Defaults match the head-office page size and metric count. */
export const ViewAllComparisonSkeleton = ({
  rows = 5,
  cols = 5,
}: {
  rows?: number;
  cols?: number;
}) => {
  const gridCols = { gridTemplateColumns: `130px repeat(${cols}, 1fr)` };
  return (
    <div className={s.vcmpScroll} aria-busy="true">
      <div className={s.vcmpInner}>
        <div className={s.vcmpHead} style={gridCols}>
          <span />
          {range(cols).map((_, i) => (
            <Bar key={i} w={54} h={9} />
          ))}
        </div>
        {range(rows).map((_, r) => (
          <div className={s.vcmpRow} style={gridCols} key={r}>
            <Bar w="76%" h={11} />
            {range(cols).map((_, c) => (
              <Bar key={c} w="100%" h={38} r={8} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ *
 * full-grid + full-page skeletons (page-level loading, before the
 * dashboard component has mounted/fetched)
 * ------------------------------------------------------------------ */

const cell = (span: string, order: number, node: React.ReactNode) => (
  <div className={classNames(grid.cell, span)} style={{ order }}>
    {node}
  </div>
);

export function HeadOfficeSkeletonGrid() {
  return (
    <div className={grid.grid} aria-busy="true">
      {cell(grid.span12, 1, <KpiSkeleton />)}
      {cell(grid.span8, 2, <GrowthSkeleton />)}
      {cell(grid.span4, 3, <MixSkeleton />)}
      {cell(grid.span8, 4, <CampusesSkeleton />)}
      {cell(grid.span4, 5, <AdoptionSkeleton />)}
      {cell(grid.span6, 6, <ListSkeleton />)}
      {cell(grid.span6, 7, <ListSkeleton />)}
      {cell(grid.span6, 8, <ContentSkeleton />)}
      {cell(grid.span6, 9, <ListSkeleton rows={4} />)}
      {/* Campus Comparison (7) + Assignment Pipeline (5) */}
      {cell(grid.span7, 10, <ComparisonSkeleton />)}
      {cell(grid.span5, 11, <MixSkeleton />)}
    </div>
  );
}

/** themed container + branding/controls + full grid */
export default function HeadOfficeDashboardSkeleton() {
  const { themeKey } = useHeadOfficeTheme();
  return (
    <div
      className={classNames(themeStyles.scope, grid.dashboard, hoFontVariables)}
      data-ho-theme={attrFor(themeKey)}
      aria-busy="true"
    >
      <div className={grid.topRow}>
        <Bar w="min(260px, 100%)" h={66} r={14} />
        <div className={s.topActions}>
          <Bar w={210} h={40} r={32} />
          <Bar w={150} h={40} r={32} />
          <Bar w={110} h={40} r={32} />
        </div>
      </div>
      <HeadOfficeSkeletonGrid />
    </div>
  );
}
